import React from 'react';

export type UsePollUntilStatus = 'done' | 'idle' | 'polling' | 'timeout';

type UsePollUntilArgs<TResult> = {
  /** Completion condition over fresh state. */
  isDone: (result: TResult) => boolean;
  /**
   * Fetches FRESH state (e.g. `queryClient.fetchQuery(...)` or
   * `(await refetch()).data`). The result is passed into `isDone`.
   *
   * WARNING: `fetchQuery` must be given `staleTime: 0` — the global default
   * (2 min) would otherwise serve the cache without a network call, so the
   * poll would never see the change (R-AUD-55).
   */
  poll: () => Promise<TResult>;
  /** Interval between attempts; default 2s (fe-plan §2.4). */
  intervalMs?: number;
  /** Total budget; after it elapses, status becomes `timeout` ("check back later"). */
  timeoutMs?: number;
  /** Polling starts once this becomes `true` (e.g. `?setup=success` marker). */
  enabled: boolean;
  /**
   * A change starts a fresh attempt with a new `timeoutMs` budget — for a
   * caller whose `enabled` stays `true` while WHAT it waits for changes (a
   * new item joins the wait after an earlier attempt ran out).
   */
  restartKey?: string;
};

/**
 * Polling until a condition is met — the return pattern from a hosted redirect
 * (fe-plan §2.4): the FE doesn't know the outcome until the webhook settles
 * state on the BE, so the relevant query is checked at an interval until the
 * change appears.
 *
 * Note: `poll`/`isDone` are read through a ref — function identity doesn't
 * restart the loop, so callers don't need to memoize them.
 */
export function usePollUntil<TResult>({
  enabled,
  intervalMs = 2000,
  isDone,
  poll,
  restartKey,
  timeoutMs = 60000,
}: UsePollUntilArgs<TResult>) {
  const [status, setStatus] = React.useState<UsePollUntilStatus>('idle');

  const callbacksRef = React.useRef({ isDone, poll });
  callbacksRef.current = { isDone, poll };

  React.useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return;
    }

    let isActive = true;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();
    setStatus('polling');

    const tick = async () => {
      if (!isActive) return;
      try {
        const result = await callbacksRef.current.poll();
        if (!isActive) return;
        if (callbacksRef.current.isDone(result)) {
          setStatus('done');
          return;
        }
      } catch {
        // A transient poll failure doesn't break the loop — the timeout budget decides.
      }
      if (Date.now() - startedAt >= timeoutMs) {
        setStatus('timeout');
        return;
      }
      timeoutId = setTimeout(tick, intervalMs);
    };

    tick().catch(() => {
      // tick swallows all errors internally; catch is here for no-floating-promises.
    });

    return () => {
      isActive = false;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [enabled, intervalMs, restartKey, timeoutMs]);

  return { status };
}
